import React, { useContext, useEffect, useState } from 'react';

import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import AddIcon from '@material-ui/icons/Add';

import {
  getCostAdjustments,
  getCostAdjustmentScopes,
  saveCostAdjustment,
  deleteCostAdjustment,
} from '../../../Actions';
import { AlertContext } from '../../../Context/AlertContextProvider';
import { formatDttm } from '../../../Util/timezone';

// Cost Adjustment Rules on the Cost Adjustments page: scoped cost "landing"
// rules (cost_adjustment table) that bump cost by a percent and/or flat $
// before margin, and can supply the margin for their scope. Every scope field
// left as "All" is a wildcard; when rules overlap, the MOST SPECIFIC rule
// (most scope fields set) wins — the server reports overlaps on save so we
// can tell the admin which rules shadow each other.

const EMPTY_RULE = {
  cost_adjustment_id: null,
  manufacturer_uuid: '',
  distributor_uuid: '',
  role_id: '',
  material_class_cd: '',
  material_sub_class_cd: '',
  size_cd: '',
  adjustment_percent: '',
  margin: '',
  add_to_cost: '',
  effective_start: '',
  effective_end: '',
};

// mysql DATE/DATETIME string -> the yyyy-mm-dd an <input type="date"> wants.
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const formatNumber = (value) =>
  value === null || value === undefined || value === '' ? null : parseFloat(Number(value).toFixed(3));

const CostAdjustmentRules = () => {
  const { handleAlertOpen, setMessageType, setMessage } = useContext(AlertContext);

  const [rules, setRules] = useState(null);
  const [scopes, setScopes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_RULE);
  const [saving, setSaving] = useState(false);

  const loadRules = () => {
    setLoading(true);
    Promise.all([getCostAdjustments(), scopes ? Promise.resolve(null) : getCostAdjustmentScopes()])
      .then(([rulesRes, scopesRes]) => {
        setRules(rulesRes.data?.rules || []);
        if (scopesRes) setScopes(scopesRes.data);
      })
      .catch(() => {
        setMessage('Error loading cost adjustment rules');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoading(false));
  };

  useEffect(loadRules, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openEditor = (rule) => {
    if (rule) {
      setDraft({
        cost_adjustment_id: rule.cost_adjustment_id,
        manufacturer_uuid: rule.manufacturer_uuid || '',
        distributor_uuid: rule.distributor_uuid || '',
        role_id: rule.role_id ?? '',
        material_class_cd: rule.material_class_cd || '',
        material_sub_class_cd: rule.material_sub_class_cd || '',
        size_cd: rule.size_cd || '',
        adjustment_percent: formatNumber(rule.adjustment_percent) ?? '',
        margin: formatNumber(rule.margin) ?? '',
        add_to_cost: formatNumber(rule.add_to_cost) ?? '',
        effective_start: toDateInput(rule.effective_start),
        effective_end: toDateInput(rule.effective_end),
      });
    } else {
      setDraft(EMPTY_RULE);
    }
    setEditorOpen(true);
  };

  const setField = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  const hasValue =
    draft.adjustment_percent !== '' || draft.margin !== '' || draft.add_to_cost !== '';

  const handleSave = () => {
    setSaving(true);
    saveCostAdjustment(draft)
      .then((res) => {
        setEditorOpen(false);
        const overlapCount = res.data?.overlaps?.length || 0;
        if (overlapCount) {
          setMessage(
            `Rule saved. It overlaps ${overlapCount} other rule${overlapCount > 1 ? 's' : ''} — where they overlap, the most specific rule wins.`
          );
          setMessageType('warning');
        } else {
          setMessage('Cost adjustment rule saved');
          setMessageType('success');
        }
        handleAlertOpen();
        loadRules();
      })
      .catch((err) => {
        setMessage(err.response?.data?.message || 'Error saving cost adjustment rule');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (rule) => {
    if (!window.confirm('Delete this cost adjustment rule? Prices using it change immediately.')) {
      return;
    }
    deleteCostAdjustment(rule.cost_adjustment_id)
      .then(() => {
        setMessage('Cost adjustment rule deleted');
        setMessageType('success');
        handleAlertOpen();
        loadRules();
      })
      .catch(() => {
        setMessage('Error deleting cost adjustment rule');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  const scopeText = (rule) => {
    const parts = [];
    if (rule.manufacturer_name) parts.push(rule.manufacturer_name);
    if (rule.distributor_name) parts.push(rule.distributor_name);
    if (rule.role_name) parts.push(`Role: ${rule.role_name}`);
    if (rule.material_class) parts.push(rule.material_class);
    if (rule.material_sub_class) parts.push(rule.material_sub_class);
    if (rule.size_cd) parts.push(`Size: ${rule.size_cd}`);
    return parts.length ? parts.join(' · ') : 'All products';
  };

  const specificity = (rule) =>
    ['manufacturer_uuid', 'distributor_uuid', 'role_id', 'material_class_cd', 'material_sub_class_cd', 'size_cd']
      .filter((f) => rule[f] !== null && rule[f] !== undefined && rule[f] !== '').length;

  const scopeSelect = (label, field, options, valueKey, labelKey) => (
    <FormControl style={{ minWidth: 170, flex: 1 }}>
      <InputLabel shrink>{label}</InputLabel>
      <NativeSelect value={draft[field]} onChange={setField(field)}>
        <option value="">All</option>
        {(options || []).map((o) => (
          <option key={o[valueKey]} value={o[valueKey]}>
            {o[labelKey]}
          </option>
        ))}
      </NativeSelect>
    </FormControl>
  );

  // Sub-classes narrow to the chosen material class.
  const subClassOptions = (scopes?.material_sub_classes || []).filter(
    (sc) => !draft.material_class_cd || sc.material_class_cd === draft.material_class_cd
  );

  return (
    <Accordion id="tour-cost-adjustments" elevation={5} style={{ margin: '0 10px 20px' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} id="tour-cost-adjustments-header">
        <div>
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Cost Adjustment Rules
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Scoped rules that adjust cost (freight, landing, surcharges) or set a margin for part
            of your catalog — by manufacturer, distributor, role, material class, or size.
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails style={{ flexDirection: 'column', paddingTop: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Typography variant="body2" color="textSecondary">
            A field left as "All" matches everything. When rules overlap, the most specific rule
            (the one with the most fields set) is the one applied.
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => openEditor(null)}
            disabled={!scopes}
          >
            Add Rule
          </Button>
        </div>

        {loading && !rules ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <CircularProgress size={28} />
          </div>
        ) : null}

        {rules && !rules.length ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: '16px 0' }}>
            No cost adjustment rules yet. Costs flow through unadjusted.
          </Typography>
        ) : null}

        {rules && rules.length ? (
          <TableContainer style={{ marginTop: 12, maxHeight: '50vh' }}>
            <Table size="small" stickyHeader aria-label="cost adjustment rules">
              <TableHead>
                <TableRow>
                  {['Applies To', 'Cost Adjustment %', 'Add to Cost $', 'Margin %', 'Effective', 'Last Modified', ''].map((h) => (
                    <TableCell key={h} style={{ backgroundColor: '#fafafa', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.map((rule) => (
                  <TableRow key={rule.cost_adjustment_id} hover>
                    <TableCell>
                      {scopeText(rule)}
                      {specificity(rule) === 0 ? (
                        <Typography variant="caption" display="block" color="textSecondary">
                          Catch-all — any more specific rule beats this one.
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{formatNumber(rule.adjustment_percent) !== null ? `${formatNumber(rule.adjustment_percent)}%` : '—'}</TableCell>
                    <TableCell>{formatNumber(rule.add_to_cost) !== null ? `$${formatNumber(rule.add_to_cost)}` : '—'}</TableCell>
                    <TableCell>{formatNumber(rule.margin) !== null ? `${formatNumber(rule.margin)}%` : '—'}</TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {toDateInput(rule.effective_start) || 'Always'} → {toDateInput(rule.effective_end) || 'No end'}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>
                      {rule.last_modified_dttm ? formatDttm(rule.last_modified_dttm) : '—'}
                      {rule.last_updated_by_name ? (
                        <Typography variant="caption" display="block" color="textSecondary">
                          by {rule.last_updated_by_name}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }} align="right">
                      <Button size="small" onClick={() => openEditor(rule)}>
                        Edit
                      </Button>
                      <Button size="small" style={{ color: '#f44336' }} onClick={() => handleDelete(rule)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </AccordionDetails>

      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {draft.cost_adjustment_id ? 'Edit Cost Adjustment Rule' : 'New Cost Adjustment Rule'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Choose who the rule applies to — every field left as "All" is a wildcard.
          </Typography>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            {scopeSelect('Manufacturer', 'manufacturer_uuid', scopes?.manufacturers, 'manufacturer_uuid', 'manufacturer_name')}
            {scopeSelect('Distributor', 'distributor_uuid', scopes?.distributors, 'distributor_uuid', 'distributor_name')}
            {scopeSelect('Role', 'role_id', scopes?.roles, 'role_id', 'role_name')}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            {scopeSelect('Material Class', 'material_class_cd', scopes?.material_classes, 'material_class_cd', 'material_class')}
            {scopeSelect('Sub-class', 'material_sub_class_cd', subClassOptions, 'material_sub_class_cd', 'material_sub_class')}
            <FormControl style={{ minWidth: 170, flex: 1 }}>
              <InputLabel shrink>Size</InputLabel>
              <NativeSelect value={draft.size_cd} onChange={setField('size_cd')}>
                <option value="">All</option>
                {(scopes?.sizes || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </NativeSelect>
            </FormControl>
          </div>

          <Typography variant="body2" color="textSecondary" gutterBottom>
            What the rule does — set at least one. These are the "Cost Adjustment" steps in How
            Your Prices Are Calculated: Cost Adjustment % and Add to Cost $ raise the cost before
            margin; Margin % (if set) is used instead of the material class / company margin.
          </Typography>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <TextField
              label="Cost Adjustment %"
              type="number"
              value={draft.adjustment_percent}
              onChange={setField('adjustment_percent')}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              style={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label="Add to Cost $ / unit"
              type="number"
              value={draft.add_to_cost}
              onChange={setField('add_to_cost')}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              style={{ flex: 1, minWidth: 150 }}
            />
            <TextField
              label="Margin %"
              type="number"
              value={draft.margin}
              onChange={setField('margin')}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              style={{ flex: 1, minWidth: 150 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <TextField
              label="Effective From"
              type="date"
              value={draft.effective_start}
              onChange={setField('effective_start')}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = starts immediately"
              style={{ flex: 1, minWidth: 170 }}
            />
            <TextField
              label="Effective Until"
              type="date"
              value={draft.effective_end}
              onChange={setField('effective_end')}
              InputLabelProps={{ shrink: true }}
              helperText="Blank = runs until removed"
              style={{ flex: 1, minWidth: 170 }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !hasValue}
          >
            {saving ? 'Saving…' : 'Save Rule'}
          </Button>
        </DialogActions>
      </Dialog>
    </Accordion>
  );
};

export default CostAdjustmentRules;
